import { IRootScopeService } from "angular";

import { IConfidenceSettings, IConfidenceMapping } from "@gtmhub/core";
import { AccountService } from "@gtmhub/accounts/accountsService";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import colors from "wwwroot/json/colors.json";
import { filterConfidenceMapping, isOutOfRange, isInvalid, isAlreadyExisting } from "../utils/utils";
import { IModalScope } from "angular-ui-bootstrap";
import { maxValueMajorScale, maxValueMinorScale, IMaxConfidenceValue } from "@gtmhub/accounts/accountsService";

export type ConfidenceScale = "minor" | "major";
export type ConfidenceType = "emoji" | "numeric" | "text";

export interface IConfidenceSettingsRootScopeService extends IRootScopeService {
  confidenceScale: ConfidenceScale;
  confidenceSettings: IConfidenceSettings;
}
export interface IConfidenceSettingsScope extends IModalScope, IMaxConfidenceValue {
  settings: IConfidenceSettings;
  colors: string[];
  emojis: string[];
  newRange: number;
  errorMessageRange: string;
  formErrorMessage: string;
  min: 0;
  step: 0.1 | 1;

  checkSettings(confidenceType?: string);
  addMapping(confidenceType: ConfidenceType): void;
  removeMapping(number: number): void;
  getScaled(number: number): number;
  save(): void;
}

export class ConfidenceSettingsCtrl {
  public static $inject = ["$rootScope", "$scope", "AccountService", "AccountResolverService"];

  constructor(
    private $rootScope: IConfidenceSettingsRootScopeService,
    private $scope: IConfidenceSettingsScope,
    private accountService: AccountService,
    private accountResolverService: AccountResolverService
  ) {
    this.init();
  }
  private defaultColor = "#888888";
  private defaultNames: string[] = [
    "High",
    "Medium",
    "Low",
    "Zero",
    "Enter label",
    "Enter label",
    "Enter label",
    "Enter label",
    "Enter label",
    "Enter label",
    "Enter label",
  ];
  private emojiString = "emoji";
  private numericString = "numeric";
  private textString = "text";
  private confidenceScale: ConfidenceScale = this.accountService.getAccountSetting("confidenceScale");

  private errorMessages = {
    input: {
      minor: {
        general: "please_enter_value_between_0_1_step_0_1",
      },
      major: {
        general: "please_enter_value_between_0_10_step_1",
      },
      alreadyAdded: "this_value_has_been_added_already",
    },
    form: {
      minor: {
        cannotDeleteTop: "range_with_value_1_cannot_be_deleted",
        topRange: "you_need_to_define_range_with_value_1",
      },
      major: {
        cannotDeleteTop: "range_with_value_10_cannot_be_deleted",
        topRange: "you_need_to_define_range_with_value_10",
      },
      validRanges: "please_enter_valid_ranges",
      removeEqual: "please_remove_equal_ranges_before_saving",
    },
  };

  private init = () => {
    this.$scope.colors = colors.colorScaleAndConfidenceLevelColors;
    this.$scope.checkSettings = this.checkSettings;
    this.$scope.addMapping = this.addMapping;
    this.$scope.removeMapping = this.removeMapping;
    this.$scope.save = this.save;
    this.$scope.getScaled = this.getScaled;
    this.$scope.emojis = [
      "smile",
      "smirk",
      "grinning",
      "worried",
      "weary",
      "expressionless",
      "disappointed",
      "collision",
      "shrugging",
      "fire",
      "thumbsup",
      "thumbsdown",
      "ok-hand",
      "punch",
      "fist",
      "clap",
      "muscle",
      "dancer",
      "see-no-evil",
      "hear-no-evil",
      "speak-no-evil",
      "speech-balloon",
      "sunny",
      "cloud",
      "zap",
      "umbrella",
      "ghost",
      "x",
      "o",
      "double-exclamation-mark",
      "poop",
      "heart",
      "four-leaf-clover",
      "bulb",
      "guitar",
      "white-check-mark",
      "red-circle",
      "large-orange-diamond",
      "one-oclock",
      "100",
      "cake",
      "lemon",
      "wine-glass",
      "beer",
      "tada",
      "sun-with-face",
      "star",
      "cry",
      "sob",
      "joy",
      "triumph",
      "rage",
    ];

    // getting the persisted confidence setting
    this.checkSettings();
    this.setScaleSettings(this.confidenceScale);
  };

  private isScalingMinor = (): boolean => {
    return this.confidenceScale === "minor";
  };

  private setScaleSettings = (confidenceScale: ConfidenceScale): void => {
    this.$scope.min = 0;

    if (confidenceScale === "major") {
      this.$scope.step = 1;
      this.$scope.newRange = 5;
      this.$scope.max = maxValueMajorScale;
    }

    if (confidenceScale === "minor") {
      this.$scope.step = 0.1;
      this.$scope.newRange = 0.5;
      this.$scope.max = maxValueMinorScale;
    }
  };

  private checkSettings = (confidenceType?: ConfidenceType) => {
    this.accountService.checkConfidenceSettings();

    const confidenceSettings: IConfidenceSettings = angular.copy(this.$rootScope.confidenceSettings);

    // if user has chaged confidence type from confidence settings view --> use his ranges with default values
    if (confidenceType && confidenceSettings && confidenceSettings.confidenceType !== this.$scope.settings.confidenceType) {
      this.useUserDefinedRanges();
    } else {
      this.$scope.settings = confidenceSettings;
    }
  };

  private useUserDefinedRanges = () => {
    switch (this.$scope.settings.confidenceType) {
      case this.numericString:
        for (let i = 0; i < this.$scope.settings.confidenceMapping.length; i++) {
          this.$scope.settings.confidenceMapping[i].color = this.$scope.settings.confidenceMapping[i].color
            ? this.$scope.settings.confidenceMapping[i].color
            : this.defaultColor;
          this.$scope.settings.confidenceMapping[i].name = null;
        }
        break;

      case this.textString:
        for (let i = 0; i < this.$scope.settings.confidenceMapping.length; i++) {
          this.$scope.settings.confidenceMapping[i].color = this.$scope.settings.confidenceMapping[i].color
            ? this.$scope.settings.confidenceMapping[i].color
            : this.defaultColor;
          this.$scope.settings.confidenceMapping[i].name = this.defaultNames[i];
        }
        break;

      case this.emojiString:
        for (let i = 0; i < this.$scope.settings.confidenceMapping.length; i++) {
          this.$scope.settings.confidenceMapping[i].color = null;
          this.$scope.settings.confidenceMapping[i].name = this.$scope.emojis[i];
        }
        break;

      default:
        break;
    }
  };

  private addMapping = (confidenceType: ConfidenceType) => {
    if (!this.validateRanges()) {
      return;
    }

    const newMapping = {
      name: null,
      color: null,
      range: {
        to: this.$scope.newRange,
      },
    };

    switch (confidenceType) {
      case this.numericString:
        newMapping.color = this.defaultColor;
        break;

      case this.textString:
        newMapping.color = this.defaultColor;
        newMapping.name = this.defaultNames[this.$scope.settings.confidenceMapping.length];
        break;

      case this.emojiString:
        newMapping.name = this.$scope.emojis[this.$scope.settings.confidenceMapping.length];
        break;

      default:
        break;
    }

    newMapping.range.to = this.isScalingMinor() ? newMapping.range.to : newMapping.range.to / 10;
    this.$scope.settings.confidenceMapping.push(newMapping);
  };

  private removeMapping = (value: number): void => {
    if (value === 1) {
      this.$scope.formErrorMessage = this.isScalingMinor() ? this.errorMessages.form.minor.cannotDeleteTop : this.errorMessages.form.major.cannotDeleteTop;
      return;
    }

    this.$scope.settings.confidenceMapping = filterConfidenceMapping(this.$scope.settings.confidenceMapping, value);
  };

  private validateRanges = (): boolean => {
    const errorMessage = this.isScalingMinor() ? this.errorMessages.input.minor.general : this.errorMessages.input.major.general;
    const range = this.$scope.newRange;

    if (isInvalid(range) || isOutOfRange(range, this.$scope.min, this.$scope.max)) {
      this.$scope.errorMessageRange = errorMessage;
      return false;
    }

    if (isAlreadyExisting(this.isScalingMinor() ? range : range / 10, this.$scope.settings.confidenceMapping)) {
      this.$scope.errorMessageRange = this.errorMessages.input.alreadyAdded;
      return false;
    }

    this.$scope.errorMessageRange = null;
    return true;
  };

  private validateSettings = (): boolean => {
    // sorting mappings from greatest to lowest to be sure the collection is ordered
    this.$scope.settings.confidenceMapping = angular
      .copy(this.$scope.settings.confidenceMapping)
      .sort((a: IConfidenceMapping, b: IConfidenceMapping) => this.getScaled(b.range.to) - this.getScaled(a.range.to));
    const mappings: IConfidenceMapping[] = this.$scope.settings.confidenceMapping;

    if (!mappings) {
      this.$scope.formErrorMessage = this.errorMessages.form.validRanges;
      return false;
    }

    if (!mappings.length) {
      this.$scope.formErrorMessage = this.isScalingMinor() ? this.errorMessages.form.minor.topRange : this.errorMessages.form.major.topRange;
      return false;
    }

    for (let i = 1; i < mappings.length; i++) {
      if (mappings[i - 1] === mappings[i]) {
        this.$scope.formErrorMessage = this.errorMessages.form.removeEqual;
        return false;
      }
    }

    return true;
  };

  private save = () => {
    if (!this.validateSettings()) {
      return;
    }

    const count = this.$scope.settings.confidenceMapping.length;

    // setting element's "from" property to be equal to next element's "to" property
    // ranges 1 and 0,5 will result to range from 1 to 0,5 and range from 0,5 to 0
    for (let i = 0; i < count - 1; i++) {
      this.$scope.settings.confidenceMapping[i].range.from = this.$scope.settings.confidenceMapping[i + 1].range.to;
    }
    // setting lowest range from property to equal 0
    this.$scope.settings.confidenceMapping[count - 1].range.from = 0;

    this.accountService.setConfidenceSettings(this.accountResolverService.getAccountId(), this.$scope.settings).then(
      () => {
        this.$scope.$dismiss();
      },
      (error) => {
        console.error(error);
      }
    );
  };

  private getScaled = (number: number): number => {
    return this.isScalingMinor() ? number : number * 10;
  };
}
